import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Collapse,
  TextField,
  MenuItem,
  Grid,
  Alert,
  LinearProgress,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import {
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  Visibility as ViewDetailsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import apiService from '../services/api';
import OutageDetailsModal from '../components/OutageDetailsModal';
import NbnOutageFormDialog from '../components/NbnOutageFormDialog';

interface Outage {
  id: string;
  incident_id: string;
  title: string;
  description: string;
  status: string;
  start_time: string;
  estimated_resolution?: string;
  resolved_at?: string;
  incident_type: string;
  location_area: string;
  avc_id?: string;
  provider_service_id?: string;
  severity?: string;
  impact_description?: string;
  resolution_description?: string;
  affected_services?: string[] | string;
  affected_areas?: string[] | string;
  source_system?: string;
  upstream_carrier?: string;
  notes?: string;
  planned?: boolean;
  updated_at?: string;
  created_at: string;
}

interface OutageUpdate {
  id: string;
  update_sequence: number;
  status: string;
  start_date?: string;
  end_date?: string;
  location: string;
  description: string;
  source_file: string;
  created_at: string;
}

interface OutageDetails extends Outage {
  updates: OutageUpdate[];
  total_updates: number;
}

const NBNOutagesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [outages, setOutages] = useState<Outage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [outageDetails, setOutageDetails] = useState<Record<string, OutageDetails>>({});
  
  // Filters - default to show active and planned outages
  const [statusFilter, setStatusFilter] = useState<string>('active,planned');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [locationFilter, setLocationFilter] = useState<string>('');
  const [networkFilter, setNetworkFilter] = useState<string>('');
  
  // Modal state for outage details
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedIncidentId, setSelectedIncidentId] = useState<string>("");
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedOutage, setSelectedOutage] = useState<Outage | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Outage | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchOutages = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      // This page covers all internet outages, but the API defaults to
      // service_type='nbn' alone for backward compatibility with other
      // callers — request every internet service type explicitly.
      params.append('service_type', 'nbn,supa_networks');
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter) params.append('incident_type', typeFilter);
      if (locationFilter) params.append('location', locationFilter);
      if (networkFilter) params.append('network', networkFilter);

      const data = await apiService.getOutages(params);
      // Add safety check for data
      const safeOutages = Array.isArray(data) ? data.filter((outage) => outage && outage.id) : [];
      setOutages(safeOutages);
      setError('');
    } catch (err: any) {
      setError('Failed to load outages');
      console.error('Error fetching outages:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, locationFilter, networkFilter]);

  useEffect(() => {
    fetchOutages();
  }, [fetchOutages]);

  // Handle incident_id query parameter to auto-open modal
  useEffect(() => {
    const incidentIdParam = searchParams.get('incident_id');
    if (incidentIdParam && outages.length > 0) {
      // Find the outage by incident_id
      const outage = outages.find((o) => o.incident_id === incidentIdParam);
      if (outage) {
        // Use incident_id for NBN outages modal
        setSelectedIncidentId(outage.incident_id);
        setModalOpen(true);
        // Remove the query parameter from URL to clean it up
        searchParams.delete('incident_id');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [outages, searchParams, setSearchParams]);

  const fetchOutageDetails = async (incidentId: string) => {
    try {
      const details = await apiService.getOutageDetails(incidentId);
      setOutageDetails(prev => ({ ...prev, [incidentId]: details }));
    } catch (err: any) {
      console.error('Error fetching outage details:', err);
    }
  };

  const handleRowToggle = (incidentId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(incidentId)) {
      newExpanded.delete(incidentId);
    } else {
      newExpanded.add(incidentId);
      // Fetch details if not already loaded
      if (!outageDetails[incidentId]) {
        fetchOutageDetails(incidentId);
      }
    }
    setExpandedRows(newExpanded);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'error';
      case 'resolved':
        return 'success';
      case 'planned':
        return 'info';
      case 'investigating':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getTypeColor = (type: string) => {
    return type === 'planned' ? 'info' : 'warning';
  };

  const formatDateTime = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm');
    } catch {
      return dateString;
    }
  };

  const formatDuration = (startTime: string, endTime?: string) => {
    try {
      const start = new Date(startTime);
      const end = endTime ? new Date(endTime) : new Date();
      const diffMinutes = Math.floor((end.getTime() - start.getTime()) / (1000 * 60));
      
      if (diffMinutes < 60) return `${diffMinutes}m`;
      if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ${diffMinutes % 60}m`;
      return `${Math.floor(diffMinutes / 1440)}d ${Math.floor((diffMinutes % 1440) / 60)}h`;
    } catch {
      return 'N/A';
    }
  };

  // Modal handlers
  const handleViewDetails = (incidentId: string) => {
    if (!incidentId || incidentId.trim() === "") {
      console.error("Invalid incident ID provided to handleViewDetails:", incidentId);
      return;
    }
    setSelectedIncidentId(incidentId);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedIncidentId("");
  };

  const handleCreateOutage = () => {
    setFormMode('create');
    setSelectedOutage(null);
    setFormOpen(true);
  };

  const handleEditOutage = (outage: Outage) => {
    setFormMode('edit');
    setSelectedOutage(outage);
    setFormOpen(true);
  };

  const handleDeleteOutage = (outage: Outage) => {
    setDeleteTarget(outage);
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteOutage = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteLoading(true);
      setDeleteError(null);
      await apiService.deleteNbnOutage(deleteTarget.id);
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      await fetchOutages();
    } catch (err: any) {
      setDeleteError(err.response?.data?.detail || err.message || 'Failed to delete outage');
    } finally {
      setDeleteLoading(false);
    }
  };

  const getSeverityColor = (severity?: string) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const handleClearFilters = () => {
    setStatusFilter('active,planned');
    setTypeFilter('');
    setLocationFilter('');
    setNetworkFilter('');
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Internet Outages
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
        Monitor internet service outages and incidents across all networks
      </Typography>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Filters
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleCreateOutage}
              >
                Create Outage
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleClearFilters}
                disabled={statusFilter === 'active,planned' && !typeFilter && !locationFilter && !networkFilter}
              >
                Clear Filters
              </Button>
            </Box>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <TextField
                select
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                fullWidth
                size="small"
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="active,planned">Active & Planned</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="planned">Planned</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                select
                label="Type"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                fullWidth
                size="small"
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="unplanned">Unplanned</MenuItem>
                <MenuItem value="planned">Planned</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                select
                label="Network"
                value={networkFilter}
                onChange={(e) => setNetworkFilter(e.target.value)}
                fullWidth
                size="small"
              >
                <MenuItem value="">All Networks</MenuItem>
                <MenuItem value="NBN Co">NBN Co</MenuItem>
                <MenuItem value="Vocus">Vocus</MenuItem>
                <MenuItem value="Supa Networks">Supa Networks</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Location"
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                fullWidth
                size="small"
                placeholder="Search location..."
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Outages Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={50}></TableCell>
              <TableCell>Incident ID</TableCell>
              <TableCell>Network</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Severity</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Start Time</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Description</TableCell>
              <TableCell width={150}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {outages.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No outages found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              outages.map((outage) => (
                <React.Fragment key={outage.incident_id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleRowToggle(outage.incident_id)}
                      >
                        {expandedRows.has(outage.incident_id) ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {outage.incident_id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {outage.upstream_carrier || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={outage.status.toUpperCase()}
                        color={getStatusColor(outage.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={outage.incident_type.toUpperCase()}
                        color={getTypeColor(outage.incident_type)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={(outage.severity || 'medium').toUpperCase()}
                        color={getSeverityColor(outage.severity)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <LocationIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {outage.location_area}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <ScheduleIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {formatDateTime(outage.start_time)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDuration(outage.start_time, outage.resolved_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={outage.description}>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 300,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {outage.description}
                        </Typography></Tooltip>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(outage.incident_id || outage.id || "unknown")}
                            color="primary"
                          >
                            <ViewDetailsIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Outage">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleEditOutage(outage)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Outage">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteOutage(outage)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>

                  {/* Expanded Row - Incident Details */}
                  <TableRow>
                    <TableCell colSpan={11} sx={{ py: 0 }}>
                      <Collapse in={expandedRows.has(outage.incident_id)} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2, backgroundColor: 'grey.50' }}>
                          {outageDetails[outage.incident_id] ? (
                            <Box>
                              <Typography variant="h6" gutterBottom>
                                Incident Details
                              </Typography>
                              
                              <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Technical Information
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    <strong>AVC ID:</strong> {outageDetails[outage.incident_id].avc_id}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    <strong>Provider Service:</strong> {outageDetails[outage.incident_id].provider_service_id}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Timeline
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    <strong>Created:</strong> {formatDateTime(outageDetails[outage.incident_id].created_at)}
                                  </Typography>
                                  {outageDetails[outage.incident_id].resolved_at && (
                                    <Typography variant="body2" color="text.secondary">
                                      <strong>Resolved:</strong> {formatDateTime(outageDetails[outage.incident_id].resolved_at!)}
                                    </Typography>
                                  )}
                                </Grid>
                              </Grid>

                              {outageDetails[outage.incident_id].updates.length > 0 && (
                                <Box>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Update History ({outageDetails[outage.incident_id].total_updates} updates)
                                  </Typography>
                                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>#</TableCell>
                                          <TableCell>Status</TableCell>
                                          <TableCell>Date</TableCell>
                                          <TableCell>Description</TableCell>
              <TableCell width={50}>Actions</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {outageDetails[outage.incident_id].updates.map((update) => (
                                          <TableRow key={update.id}>
                                            <TableCell>{update.update_sequence}</TableCell>
                                            <TableCell>
                                              <Chip
                                                label={update.status}
                                                size="small"
                                                variant="outlined"
                                              />
                                            </TableCell>
                                            <TableCell>
                                              {formatDateTime(update.created_at)}
                                            </TableCell>
                                            <TableCell>
                                              <Typography variant="body2">
                                                {update.description}
                                              </Typography>
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </Box>
                              )}
                            </Box>
                          ) : (
                            <Box display="flex" alignItems="center" justifyContent="center" py={2}>
                              <LinearProgress sx={{ width: 200 }} />
                              <Typography variant="body2" sx={{ ml: 2 }}>
                                Loading details...
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <NbnOutageFormDialog
        open={formOpen}
        mode={formMode}
        outage={selectedOutage}
        onClose={() => setFormOpen(false)}
        onSuccess={async () => {
          await fetchOutages();
        }}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={deleteLoading ? undefined : () => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Internet Outage</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography>
              Are you sure you want to delete outage{' '}
              <strong>{deleteTarget?.incident_id || 'this outage'}</strong>? This action cannot be undone.
            </Typography>
            {deleteError && <Alert severity="error">{deleteError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDeleteOutage}
            disabled={deleteLoading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Outage Details Modal */}
      <OutageDetailsModal
        open={modalOpen}
        onClose={handleCloseModal}
        incidentId={selectedIncidentId}
      />
    </Box>
  );
};

export default NBNOutagesPage;
